/** @jsxImportSource @emotion/react */
import { Paper as MuiPaper, useMediaQuery, useTheme } from '@material-ui/core';
import { Buttons, TextFields } from '@zip/business-components';
import * as Icons from '@zip/icons';
import { BasePage, MerchantSelector, Tooltip } from 'components';
import { useMerchantData } from 'contexts';
import { FeatureFlag, MerchantRoleGuid, PermissionEnum } from 'enums';
import { collections, css, theme } from 'global';
import { useFeatureFlagEnabled } from 'hooks';
import _ from 'lodash';
import {
  CompleteMfaBannerModule,
  DeleteUserModule,
  NewUserModule,
  UpdateUserModule,
  UsersTableModule,
  UsersTableRef,
} from 'modules';
import { FC, FunctionComponent, useEffect, useRef, useState } from 'react';
import { MerchantUser, PageProps, UserRole } from 'types';
import { useUserFunctions } from 'utils';
import * as styles from './UsersPage.styles';

const UsersPage: FC<PageProps> = () => {
  const { checkPermission, merchantIdentity, isCompanyUser, isAdmin, hasRole } =
    useMerchantData();
  const UserFunctions = useUserFunctions();
  const { breakpoints } = useTheme();

  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const isFranchiseManagerUser = hasRole(MerchantRoleGuid.FranchiseManager);

  const usersTableRef = useRef<UsersTableRef>();
  const [roles, setRoles] = useState<UserRole[]>([]);

  const [searchValue, setSearchValue] = useState<string>();

  const handleSearchValueChange = (e): void => setSearchValue(e?.target?.value);

  // MFA

  const selectiveMfaEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableSelectiveMFA
  );

  // Dialogs
  const [userToAction, setUserToAction] = useState<MerchantUser>();
  const [isNewUserDialogOpen, setIsNewUserDialogOpen] =
    useState<boolean>(false);
  const [isUpdateUserDialogOpen, setIsUpdateUserDialogOpen] =
    useState<boolean>(false);
  const [isDeleteUserDialogOpen, setIsDeleteUserDialogOpen] =
    useState<boolean>(false);

  const handleCreateUserClick = (): void => setIsNewUserDialogOpen(true);

  const handleSuccess = (): void => usersTableRef?.current?.getUsers();

  const getUserActions = (user: MerchantUser): FunctionComponent[] => {
    const actions = [];

    const userToActionIsUneditable =
      // Below line will allow Company users to edit Merchant Users (pending risk discussion)
      // user.userType == UserType.Company &&
      collections.roles.adminRoles.includes(user.roleId as MerchantRoleGuid);

    const currentUserCanEditUserToAction =
      isCompanyUser ||
      (isAdmin &&
        !collections.roles.adminRoles.includes(
          user.roleId as MerchantRoleGuid
        )) ||
      (hasRole(MerchantRoleGuid.FranchiseManager) &&
        collections.roles.franchiseManageCanManage.includes(
          user.roleId as MerchantRoleGuid
        ));

    const isActionable =
      !userToActionIsUneditable && currentUserCanEditUserToAction;

    const iconFillColour = isActionable
      ? theme.colors.neutral[600]
      : theme.colors.neutral[200];

    actions.push(
      <Tooltip
        key={`actions-edit-${user.userProfileId}`}
        content={
          isActionable ? 'Edit' : 'Please contact Zip to edit this user.'
        }
        placement="right"
        dense
      >
        <Icons.Pencil
          width={28}
          height={28}
          fill={iconFillColour}
          css={styles.action(isActionable)}
          onClick={(): void => {
            if (isActionable) {
              setUserToAction(user);
              setIsUpdateUserDialogOpen(true);
            }
          }}
        />
      </Tooltip>,
      <Tooltip
        key={`actions-delete-${user.userProfileId}`}
        content={
          isActionable ? 'Delete' : 'Please contact Zip to delete this user.'
        }
        placement="right"
        dense
      >
        <Icons.Trash
          width={28}
          height={28}
          fill={iconFillColour}
          css={styles.action(isActionable)}
          onClick={(): void => {
            if (isActionable) {
              setUserToAction(user);
              setIsDeleteUserDialogOpen(true);
            }
          }}
        />
      </Tooltip>
    );
    return actions;
  };

  // Button to create order that is moved for mobile view
  const createUserButton = (
    <Buttons.Secondary
      css={styles.createUserButton(false)}
      onClick={handleCreateUserClick}
    >
      <Icons.UserCirclePlus width={20} height={20} />
      <span id="buttonLabel">Create user</span>
    </Buttons.Secondary>
  );

  // get roles to share across the modules
  useEffect(() => {
    async function updateRoles(): Promise<void> {
      const roles = (await UserFunctions.getAllRoles())?.roles.map((role) => ({
        name: role.name,
        roleGuid: role.roleGuid.toUpperCase(),
      }));
      const rolesToSet = _.uniqBy(roles, (role) => role.roleGuid)?.sort(
        (a, b) => (a.name?.toLowerCase()[0] < b.name?.toLowerCase()[0] ? -1 : 1)
      );
      setRoles(rolesToSet);
    }

    setRoles([]);
    updateRoles();
  }, [merchantIdentity.merchantId]);

  return (
    <>
      <CompleteMfaBannerModule adminOnly />
      <BasePage
        title="Users"
        variant="layout"
        fullWidth
        flex
        action={isMobile && createUserButton}
      >
        <MuiPaper elevation={0} css={styles.usersTableContainer(false)}>
          <div id="toolbar" css={styles.toolbar}>
            <span>
              <TextFields.Outlined
                id="searchValue"
                placeholder="Search..."
                value={searchValue}
                onChange={handleSearchValueChange}
                css={styles.input}
                startIcon={<Icons.Search />}
                endIcon={
                  searchValue && (
                    <Icons.Close
                      css={css.clearIcon}
                      onClick={handleSearchValueChange}
                    />
                  )
                }
                fullWidth={isMobile}
              />
              {isCompanyUser && selectiveMfaEnabled && !isMobile && (
                <MerchantSelector entity="merchant-only" dense={false} />
              )}

              {isFranchiseManagerUser && selectiveMfaEnabled && !isMobile && (
                <MerchantSelector entity="branch-only" dense={false} />
              )}
            </span>

            {!isMobile &&
              checkPermission(PermissionEnum.UserManagementExecute) &&
              createUserButton}
          </div>

          <UsersTableModule
            ref={usersTableRef}
            searchValue={searchValue}
            show2StepVerification={selectiveMfaEnabled}
            getUserActions={getUserActions}
          />
        </MuiPaper>
      </BasePage>

      <NewUserModule
        roles={roles}
        open={isNewUserDialogOpen}
        toggleOpen={setIsNewUserDialogOpen}
        onSuccess={handleSuccess}
      />
      <UpdateUserModule
        user={userToAction}
        roles={roles}
        open={isUpdateUserDialogOpen}
        toggleOpen={setIsUpdateUserDialogOpen}
        onSuccess={handleSuccess}
      />
      <DeleteUserModule
        user={userToAction}
        open={isDeleteUserDialogOpen}
        toggleOpen={setIsDeleteUserDialogOpen}
        onSuccess={handleSuccess}
      />
    </>
  );
};

export default UsersPage;
